/**
 * Template Versioning GraphQL Operations
 *
 * Phase 3: Template versioning with diff and rollback capabilities
 * Backend: graphql_gateway/src/metadata_api/template_version_graphql.rs
 */

import { gql } from '@apollo/client';

// ============================================================================
// QUERIES
// ============================================================================

export const LIST_TEMPLATE_VERSIONS = gql`
  query ListTemplateVersions($templateId: String!) {
    listTemplateVersions(templateId: $templateId) {
      versionId
      templateId
      tenantId
      versionNumber
      templateSnapshot
      changeDescription
      previousVersionId
      status
      createdAt
      createdBy
    }
  }
`;

export const GET_TEMPLATE_VERSION = gql`
  query GetTemplateVersion($templateId: String!, $versionNumber: Int!) {
    getTemplateVersion(templateId: $templateId, versionNumber: $versionNumber) {
      versionId
      templateId
      tenantId
      versionNumber
      templateSnapshot
      changeDescription
      previousVersionId
      status
      createdAt
      createdBy
    }
  }
`;

export const GET_TEMPLATE_VERSION_DIFF = gql`
  query GetTemplateVersionDiff($templateId: String!, $fromVersion: Int!, $toVersion: Int!) {
    getTemplateVersionDiff(templateId: $templateId, fromVersion: $fromVersion, toVersion: $toVersion) {
      fromVersion
      toVersion
      changes {
        fieldPath
        oldValue
        newValue
        changeType
      }
      summary
    }
  }
`;

export const GET_TEMPLATE_VERSION_HISTORY = gql`
  query GetTemplateVersionHistory($templateId: String!) {
    getTemplateVersionHistorySummary(templateId: $templateId) {
      templateId
      totalVersions
      firstVersion
      currentVersion
      createdAt
      lastModifiedAt
      lastModifiedBy
      versions {
        versionNumber
        createdAt
        createdBy
        changeDescription
        status
      }
    }
  }
`;

// ============================================================================
// MUTATIONS
// ============================================================================

export const ROLLBACK_TEMPLATE = gql`
  mutation RollbackTemplate($input: RollbackTemplateInput!) {
    rollbackTemplate(input: $input) {
      success
      newVersionNumber
      newVersionId
      errorMessage
    }
  }
`;

// ============================================================================
// TYPES
// ============================================================================

export interface TemplateVersion {
  versionId: string;
  templateId: string;
  tenantId: string;
  versionNumber: number;
  templateSnapshot: unknown; // Complete template JSON
  changeDescription: string;
  previousVersionId: string | null;
  status: 'Draft' | 'Active' | 'Superseded' | 'Deprecated';
  createdAt: string;
  createdBy: string;
}

export interface FieldChange {
  fieldPath: string;
  oldValue: unknown;
  newValue: unknown;
  changeType: 'Added' | 'Modified' | 'Deleted';
}

export interface TemplateVersionDiff {
  fromVersion: number;
  toVersion: number;
  changes: FieldChange[];
  summary: string;
}

export interface VersionSummaryItem {
  versionNumber: number;
  createdAt: string;
  createdBy: string;
  changeDescription: string;
  status: 'Draft' | 'Active' | 'Superseded' | 'Deprecated';
}

export interface TemplateVersionHistory {
  templateId: string;
  totalVersions: number;
  firstVersion: number;
  currentVersion: number;
  createdAt: string;
  lastModifiedAt: string;
  lastModifiedBy: string;
  versions: VersionSummaryItem[];
}

export interface RollbackTemplateInput {
  templateId: string;
  tenantId: string;
  targetVersion: number;
  rollbackReason: string;
}

export interface RollbackResult {
  success: boolean;
  newVersionNumber: number;
  newVersionId: string;
  errorMessage: string | null;
}

// ============================================================================
// QUERY/MUTATION VARIABLES
// ============================================================================

export interface ListTemplateVersionsVariables {
  templateId: string;
}

export interface GetTemplateVersionVariables {
  templateId: string;
  versionNumber: number;
}

export interface GetTemplateVersionDiffVariables {
  templateId: string;
  fromVersion: number;
  toVersion: number;
}

export interface GetTemplateVersionHistoryVariables {
  templateId: string;
}

export interface RollbackTemplateVariables {
  input: RollbackTemplateInput;
}
